import type { TFunction } from "i18next";
import type { LlmRequirements } from "../../../api/content/stepPlans";
import { lineageOf } from "../../../api/content/typeLineage";
import type { ListedConnector } from "../../../api/connectorsApi";
import type { OfferedEffort, RouteUnavailability } from "../../../api/connectorStatus";
import type { RouteOptionListing } from "../../../api/threads/routeOptions";
import { sameStarredLlm, type StarredLlm } from "../../../api/userPreferencesApi";
import { resolvePresentation } from "../../workflows/presentation/resolvePresentation";
import { optionOf, routeChoicesOf, routeFitOf, type RouteChoice, type RouteFit, type TurnRoute } from "../hosting/routeChoices";

/**
 * What the LLM picker offers (F106, Design C; T3 Code's model picker): every LLM a PC's
 * agents and API keys reported, grouped by PC and route, as discovered at run time (no list
 * in code). Whether one meets the Thread's or the step's requirements is the server's
 * verdict (`routeFitOf`); a starred LLM is the person's, keyed by its route, never by PC.
 */
export interface LlmOption {
  /** Unique in the picker: the PC, the route and the model. */
  readonly key: string;
  readonly connectorId: string;
  /** "‹PC› › ‹agent or API key›": the group it is listed under. */
  readonly group: string;
  /** The route it chooses, without a reasoning effort: the picker adds the chosen one. */
  readonly route: TurnRoute;
  /** The model's own name; for an agent's default, `null`. */
  readonly modelName: string | null;
  /** For an agent's default: the LLM it starts with, when its latest session showed it. */
  readonly defaultModelName: string | null;
  /** `null` for an agent's default, which names no model to star. */
  readonly star: StarredLlm | null;
  /** Why it cannot be chosen now, as a status to word; `null` when it can. */
  readonly unavailableBecause: RouteChoice["unavailableBecause"];
  readonly fit: RouteFit;
  /** The agent's reasoning efforts for its default LLM; empty for an API key. */
  readonly efforts: readonly OfferedEffort[];
  /** The effort "Standard" runs with, as module C answers it (the person's, else the installation's level); `null`: the agent's own. */
  readonly standardEffort: OfferedEffort | null;
}

/** One PC's latest report, and the server's route options for its routes, read together (`useLlmChoices`). */
export interface ConnectorOptions {
  readonly connector: ListedConnector;
  readonly listing: RouteOptionListing;
}

export function llmOptionsOf(connectors: readonly ConnectorOptions[]): LlmOption[] {
  return connectors.flatMap(({ connector, listing }) =>
    routeChoicesOf(connector).flatMap((choice) => {
      const group = `${connector.name} › ${choice.title}`;
      const agent = choice.kind === "agent" ? connector.status.agents.find((candidate) => candidate.agentId === choice.id) : undefined;
      const provider = choice.kind === "api_key" ? connector.status.apiRoutes.find((candidate) => candidate.routeId === choice.id)?.baseUrlHost : undefined;
      const efforts = agent?.reasoningEfforts ?? [];
      const option = (route: TurnRoute, modelName: string | null, modelId: string | null): LlmOption => ({
        key: `${connector.connectorId}|${choice.key}|${modelId ?? ""}`,
        connectorId: connector.connectorId,
        group,
        route,
        modelName,
        defaultModelName: modelId === null ? modelNameOf(choice, agent?.defaultModelId ?? null) : null,
        star: modelId === null ? null : { routeKind: choice.kind, route: choice.kind === "agent" ? choice.id : (provider ?? choice.id), modelId },
        unavailableBecause: choice.unavailableBecause,
        fit: routeFitOf(listing.options, route),
        efforts,
        standardEffort: efforts.find((effort) => effort.effortId === optionOf(listing.options, route)?.standardReasoningEffort) ?? null,
      });
      const models = choice.models.map((model) =>
        option(choice.kind === "agent" ? { kind: "agent", agent_id: choice.id, model_id: model.modelId } : { kind: "api_key", route_id: choice.id, model_id: model.modelId }, model.displayName, model.modelId),
      );
      return choice.kind === "agent" ? [option({ kind: "agent", agent_id: choice.id, model_id: null }, null, null), ...models] : models;
    }),
  );
}

function modelNameOf(choice: RouteChoice, modelId: string | null): string | null {
  if (modelId === null) return null;
  return choice.models.find((model) => model.modelId === modelId)?.displayName ?? modelId;
}

/** Whether the person starred this LLM: on its route, on any PC. */
export function isStarred(stars: readonly StarredLlm[], star: StarredLlm | null): boolean {
  return star !== null && stars.some((candidate) => sameStarredLlm(candidate, star));
}

/** A route without its reasoning effort: the server's options name routes without one. */
export function withoutEffort(route: TurnRoute): TurnRoute {
  if (route.kind !== "agent" || route.reasoning_effort === undefined) return route;
  return { kind: "agent", agent_id: route.agent_id, model_id: route.model_id };
}

/** The route with the chosen reasoning effort, or the agent's own when `null`. */
export function withEffort(route: TurnRoute, effortId: string | null): TurnRoute {
  const plain = withoutEffort(route);
  return plain.kind === "agent" && effortId !== null ? { ...plain, reasoning_effort: effortId } : plain;
}

/** Whether `option` is the route `chosen` names, whatever its reasoning effort. */
export function choosesRoute(option: LlmOption, chosen: TurnRoute | null, connectorId: string | null): boolean {
  return chosen !== null && option.connectorId === connectorId && JSON.stringify(option.route) === JSON.stringify(withoutEffort(chosen));
}

/** What the person typed matches the model, its route or its PC. */
export function matchesSearch(option: LlmOption, search: string): boolean {
  const words = search.trim().toLowerCase().split(/\s+/).filter((word) => word.length > 0);
  const text = [option.group, option.modelName ?? "", option.defaultModelName ?? "", option.route.model_id ?? ""].join(" ").toLowerCase();
  return words.every((word) => text.includes(word));
}

/** "Codex · gpt-6.1-sol · High": the route by the names its PC reported. */
export function llmLabel(route: TurnRoute, connector: ListedConnector | undefined, t: TFunction): string {
  const agent = route.kind === "agent" ? connector?.status.agents.find((candidate) => candidate.agentId === route.agent_id) : undefined;
  const models = route.kind === "agent" ? (agent?.models ?? []) : (connector?.status.apiRoutes.find((candidate) => candidate.routeId === route.route_id)?.models ?? []);
  const nameOf = (modelId: string) => models.find((model) => model.modelId === modelId)?.displayName ?? modelId;
  const owner = route.kind === "agent" ? (agent?.title ?? route.agent_id) : route.route_id;
  const model = route.model_id !== null ? nameOf(route.model_id) : agent?.defaultModelId != null ? t("threads.llm.defaultNamed", { model: nameOf(agent.defaultModelId) }) : t("threads.llm.default");
  const effort = route.kind === "agent" && route.reasoning_effort !== undefined ? (agent?.reasoningEfforts.find((candidate) => candidate.effortId === route.reasoning_effort)?.displayName ?? route.reasoning_effort) : null;
  return [owner, model, effort].filter((part) => part !== null).join(" · ");
}

/**
 * Whether the PC runs the route now, as module C decides it for dispatch (CP-11): its route
 * options answer the route already chosen too, offered or not. An LLM that isn't offered stops
 * the step; nothing falls back (F106, question 3).
 */
export function offeredOn(route: TurnRoute, options: ConnectorOptions | undefined): boolean {
  return unavailabilityOn(route, options) === null;
}

/** Why the PC doesn't run the route now, as module C serves it (CP-11, SOT-06a); a route it doesn't list isn't offered; `null` while it runs. */
export function unavailabilityOn(route: TurnRoute, options: ConnectorOptions | undefined): RouteUnavailability | null {
  const option = optionOf(options?.listing.options ?? null, withoutEffort(route));
  if (option?.available === true) return null;
  return option?.unavailableReason ?? "model_unavailable";
}

/** "‹PC›: not logged in. Choose another.": an LLM the PC doesn't run, by module C's reason. */
export function notOfferedText(pc: string, reason: RouteUnavailability, t: TFunction): string {
  return t("threads.llm.notOffered", { pc, reason: t(`threads.hosting.unavailable.${reason}`) });
}

/** Whether the registry's view for what a step runs with is the LLM picker (`presentations.ts`). */
export function picksLlm(llmRequirements: LlmRequirements): boolean {
  return resolvePresentation(lineageOf({ type: llmRequirements.routeType, many: false, segmentType: null, holds: [] }, []), "pick")?.presentation.view === "llmRoute";
}
